import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Instagram, Youtube, ArrowUpRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { Reveal } from './Reveal';

const TikTokIcon = ({ size = 18, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const Footer = () => {
  const { t, language } = useLanguage();

  const socialLinks = [
    { name: 'TikTok', Icon: TikTokIcon, url: 'https://www.tiktok.com/@renihamikus' },
    { name: 'Instagram', Icon: Instagram, url: 'http://instagram.com/renatahamikus' },
    { name: 'YouTube', Icon: Youtube, url: 'https://youtube.com/@renatahamikus' },
  ];

  const navLinks = [
    { id: 'hero', label: t.nav.home },
    { id: 'course', label: t.nav.course },
    { id: 'work-with-me', label: t.nav.workWithMe },
    { id: 'favorites', label: t.nav.favorites },
    { id: 'subscribe', label: t.nav.subscribe },
  ];

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <footer className="relative bg-text-primary text-background overflow-hidden" data-testid="footer">
      <div className="absolute inset-0 grain-layer opacity-30 pointer-events-none" />
      <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-gradient-to-br from-accent/20 to-transparent blur-3xl" />

      <div className="relative max-w-[1480px] mx-auto px-6 md:px-12 pt-24 md:pt-32 pb-10">
        {/* Top: contact / nav grid */}
        <div className="grid lg:grid-cols-12 gap-12 md:gap-16">
          {/* Column 1 — Tagline */}
          <div className="lg:col-span-5">
            <Reveal as="p" className="font-body text-[0.72rem] uppercase tracking-[0.32em] text-accent mb-6">
              {language === 'hu' ? 'Maradj velem' : 'Stay close'}
            </Reveal>
            <Reveal as="p" duration={1.1} className="font-heading italic text-3xl md:text-4xl leading-snug text-background max-w-md">
              {language === 'hu'
                ? '„Az inspiráció nem vár — utánamegyünk."'
                : '"Inspiration doesn\'t wait — we chase it."'}
            </Reveal>

            {/* Socials */}
            <div className="mt-12 flex items-center gap-5">
              {socialLinks.map((s) => (
                <a
                  key={s.name}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center justify-center w-11 h-11 rounded-full border border-background/20 hover:border-accent hover:bg-accent transition-all duration-500"
                  data-testid={`footer-social-${s.name.toLowerCase()}`}
                  aria-label={s.name}
                >
                  <s.Icon size={16} className="text-background group-hover:text-text-primary transition-colors" />
                </a>
              ))}
            </div>
          </div>

          {/* Column 2 — Nav */}
          <div className="lg:col-span-3 lg:col-start-7">
            <p className="font-body text-[0.65rem] uppercase tracking-[0.3em] text-background/50 mb-6">
              {language === 'hu' ? 'Navigáció' : 'Navigate'}
            </p>
            <ul className="space-y-3">
              {navLinks.map((l) => (
                <li key={l.id}>
                  <button
                    onClick={() => scrollTo(l.id)}
                    className="link-underline font-heading italic text-xl md:text-2xl text-background/85 hover:text-accent transition-colors duration-500"
                    data-testid={`footer-nav-${l.id}`}
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3 — External */}
          <div className="lg:col-span-3">
            <p className="font-body text-[0.65rem] uppercase tracking-[0.3em] text-background/50 mb-6">
              {language === 'hu' ? 'Hivatkozások' : 'Elsewhere'}
            </p>
            <ul className="space-y-3">
              <li>
                <a
                  href="https://www.penzkereses.online/"
                  target="_blank" rel="noopener noreferrer"
                  className="group inline-flex items-center gap-2 font-heading italic text-xl md:text-2xl text-background/85 hover:text-accent transition-colors duration-500"
                  data-testid="footer-link-course"
                >
                  {language === 'hu' ? 'A Kurzus' : 'The Course'}
                  <ArrowUpRight size={16} strokeWidth={1.5} className="opacity-60 group-hover:opacity-100 group-hover:rotate-45 transition-all duration-500" />
                </a>
              </li>
              <li>
                <a
                  href="https://www.amazon.de/hz/wishlist/ls/3T6Q2EQZRCIJ5?ref_=wl_share"
                  target="_blank" rel="noopener noreferrer"
                  className="group inline-flex items-center gap-2 font-heading italic text-xl md:text-2xl text-background/85 hover:text-accent transition-colors duration-500"
                  data-testid="footer-link-favorites"
                >
                  Amazon Wishlist
                  <ArrowUpRight size={16} strokeWidth={1.5} className="opacity-60 group-hover:opacity-100 group-hover:rotate-45 transition-all duration-500" />
                </a>
              </li>
              <li>
                <a
                  href="mailto:renimikus@gmail.com"
                  className="group inline-flex items-center gap-2 font-heading italic text-xl md:text-2xl text-background/85 hover:text-accent transition-colors duration-500"
                  data-testid="footer-link-email"
                >
                  {language === 'hu' ? 'Írj nekem' : 'Email me'}
                  <ArrowUpRight size={16} strokeWidth={1.5} className="opacity-60 group-hover:opacity-100 group-hover:rotate-45 transition-all duration-500" />
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Massive wordmark */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 1.4, ease: [0.16,1,0.3,1] }}
          className="mt-20 md:mt-28"
        >
          <button onClick={() => scrollTo('hero')} className="block w-full text-left" data-testid="footer-logo">
            <h3 className="font-heading text-background leading-[0.85] tracking-tighter text-[18vw] md:text-[14vw]">
              <span className="font-extralight">Renata </span>
              <span className="italic font-light">Hamikus</span>
              <span className="text-accent">.</span>
            </h3>
          </button>
        </motion.div>

        {/* Bottom strip */}
        <div className="mt-10 pt-6 border-t border-background/15 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="font-body text-[0.7rem] uppercase tracking-[0.25em] text-background/50">
            {t.footer.copyright}
          </p>
          <p className="font-body text-[0.7rem] uppercase tracking-[0.25em] text-background/50">
            {language === 'hu' ? 'Készült szeretettel · Magyarországon' : 'Crafted with care · in Hungary'}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
